import { useEffect, useState, useCallback } from "react";
import { Wallet } from "lucide-react";
import { api } from "../lib/api";
import { SYMBOL } from "./LogSpendModal";

/**
 * SpendHistoryStrip — small per-target strip shown to logged-in users on a
 * place/vendor profile. Renders "You have spent $120 USD here across 3 visits."
 * Hidden when there are no entries.
 *
 * Props:
 *   user: logged-in user (or null)
 *   targetType: "business" | "vendor"
 *   targetId: string
 *   refreshKey: number — bump to re-fetch
 *   onTap: optional callback (e.g. open list / modal)
 */
export default function SpendHistoryStrip({ user, targetType, targetId, refreshKey, onTap }) {
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/me/spending/summary", {
        params: { target_type: targetType, target_id: targetId },
      });
      setSummary(data);
    } catch {
      setSummary(null);
    }
  }, [user, targetType, targetId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!user || !summary || summary.entry_count === 0) return null;

  const totals = summary.totals || {};
  const parts = Object.entries(totals)
    .filter(([, v]) => v > 0)
    .map(([cur, v]) => `${SYMBOL[cur] || ""}${v.toFixed(2)} ${cur}`);
  const visitsLabel = summary.entry_count === 1 ? "1 visit" : `${summary.entry_count} visits`;

  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="spend-history-strip"
      className="w-full text-left flex items-center gap-3 px-4 py-3 mt-4 border border-gold-soft bg-[rgba(232,184,75,0.06)] rounded-sm hover:border-gold-strong transition-colors"
    >
      <span className="w-9 h-9 grid place-items-center rounded-full border border-gold-strong text-gold bg-[rgba(232,184,75,0.08)] shrink-0">
        <Wallet className="w-4 h-4" strokeWidth={1.5} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="eyebrow text-gold/90">Your spend here</div>
        <div className="text-sm text-foreground mt-0.5">
          <span className="font-mono text-gold">{parts.join(" · ") || "—"}</span>
          <span className="text-foreground/55"> across {visitsLabel}</span>
        </div>
      </div>
    </button>
  );
}
